import { type BookLiquiditySeed } from '@/stores/bookLiquiditySettings';
/**
 * D-020 LT2-017 — the nearest-three multi-line expanded view.
 *
 * When `spreadTotalNearestLines` is on, expanding a spread/total row renders
 * the SELECTED line plus its two nearest neighbors as three adjacent
 * two-sided ladders — ascending line order, the selected (center-of-window)
 * line visually emphasized, each block headed by its own line label +
 * availability badge. Blocks for unavailable lines render but stay inert
 * (TwoSidedLadderBody gates clicks on availability), so a dead line can
 * never consume an order.
 *
 * Every level click carries the BLOCK's own line index via
 * `PillTradeMeta.lineIdx`, so the pill-trade handlers synthesize the clicked
 * line's market — not the selected one (LT2-006 stable identity across the
 * window). Hotkey focus stays on the CARD: A/D keep resolving the selected
 * line only (documented in the EventRows resolver), and only the selected
 * block carries the fence's price marks (#953, see `lt2FocusId`).
 *
 * StreamRift skin (`variant="cockpit"`, Sniper only): the SELECTED block alone,
 * full width, with cockpit rungs. Three 28px ladders cannot share one Sniper
 * column, and the board's line chips already pick the line — so the neighbours
 * are one click away rather than squeezed in. The row keeps its fence marks.
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { fairPctText } from '@/lib/opOdds';
import { formatCompactCurrency } from '@/lib/infra/formatters';
import { formatVolume } from '../ValueDashboard/ladder/helpers';
import { butterflyCardContext, type ButterflyEventIdentity } from './butterflyContext';
import { cn } from '@/lib/infra/utils';
import { Td } from '@/components/atoms/TableCells';
import type { SpreadLine, TotalLine } from '../ValueDashboard/types';
import type { UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import {
  classifyLineAvailability,
  LINE_AVAILABILITY_LABEL,
} from '@/lib/sport/lineAvailability';
import { formatSignedLine, venueAwareLineWindow } from './synthesize';
import {
  BUTTERFLY_NARROW_LADDER,
  TwoSidedLadderBody,
  type LadderHue,
  type Lt2LadderAddressing,
} from './TwoSidedLadder';

/**
 * #884 — whether the three-card row is wider than its scroller. The row is
 * `overflow-x-auto`; at 1470×956 three narrow cards do not fit beside the
 * rail, and a scrollbar the platform may not paint is not a signal. When the
 * row overflows, a visible caption says so; nothing else moves.
 */
function useRowOverflow(): [React.RefObject<HTMLDivElement | null>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setOverflowing(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  });
  return [ref, overflowing];
}

/**
 * #953 — the part of `el` a trader can actually SEE, horizontally: its own
 * scrollport cut by every ancestor that clips sideways, then by the window.
 * At 1470 beside the rail the board's table is wider than its frame, so the
 * strip's own right edge is itself hidden — "inside the strip" is not enough.
 */
function visibleSpan(el: HTMLElement): { left: number; right: number } {
  const box = el.getBoundingClientRect();
  let left = box.left + el.clientLeft;
  let right = left + el.clientWidth;
  for (let a = el.parentElement; a != null; a = a.parentElement) {
    if (window.getComputedStyle(a).overflowX === 'visible') continue;
    const clip = a.getBoundingClientRect();
    const portLeft = clip.left + a.clientLeft;
    left = Math.max(left, a.clientWidth > 0 ? portLeft : clip.left);
    right = Math.min(right, a.clientWidth > 0 ? portLeft + a.clientWidth : clip.right);
  }
  return { left: Math.max(left, 0), right: Math.min(right, window.innerWidth) };
}

/**
 * #953 — bring the SELECTED block into view by scrolling the STRIP, and only
 * the strip. `nearestLineWindow` puts the block rightmost when the highest
 * line is selected, and at 1470 beside the rail it can sit wholly past the
 * visible strip — where the hotkey fence (rightly) refuses its prices.
 *
 * Deliberately not `scrollIntoView`: that scrolls EVERY scroller around the
 * block, and at 1470 it slid the whole board 51px sideways (cutting off the
 * row labels and the aim badge) and, on a short window, scrolled the line
 * strip the trader had just clicked out of view. Only the strip moves here;
 * a block below the fold stays there, and the fence says "scroll to it".
 *
 * Nearest-edge semantics: nothing moves when the block already shows; a
 * block past the right edge is brought in by its right edge, never pushing
 * its left edge out. A block WIDER than the visible strip (a narrow window)
 * cannot show whole, so the aim is the two price marks the hotkeys post
 * instead, when the card is aimed and they exist (#959 review). Runs when the
 * panel mounts (the card expanding) and when the selected line's IDENTITY
 * changes — never on a price tick, which re-renders with the same key.
 */
function useScrollSelectedIntoView(
  stripRef: React.RefObject<HTMLDivElement | null>,
  selectedKey: string | null,
) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const strip = stripRef.current;
    const block = ref.current;
    if (selectedKey == null || !strip || !block) return;
    const view = visibleSpan(strip);
    let { left, right } = block.getBoundingClientRect();
    const prices = [...block.querySelectorAll('[data-lt2-price]')].map((el) => el.getBoundingClientRect());
    if (right - left > view.right - view.left && prices.length > 0) {
      left = Math.min(...prices.map((r) => r.left));
      right = Math.max(...prices.map((r) => r.right));
    }
    let delta = 0;
    if (left < view.left) delta = left - view.left;
    else if (right > view.right) delta = Math.min(right - view.right, left - view.left);
    if (delta !== 0) strip.scrollLeft += delta;
  }, [stripRef, selectedKey]);
  return ref;
}

export interface NearestLinesPanelProps {
  /**
   * The fixture this panel's lines belong to — what makes the three blocks
   * BUTTERFLY cards (#884): each card resolves its own exact reference and
   * context from it and draws the narrow five-level book. Absent = the plain
   * nearest-three ladders (the live-game / Sniper boards): default-layout
   * ladders whose bid rungs the level cursor can address (`lt2Levels`), which
   * the narrow geometry deliberately cannot.
   */
  event?: ButterflyEventIdentity;
  period?: string;
  kind: 'spread' | 'total';
  lines: SpreadLine[] | TotalLine[];
  selectedIdx: number;
  yesLabel: string; // 'YES' | 'OVER'
  noLabel: string; // 'NO' | 'UNDER'
  makerRebatePct: number;
  /** Resolve the trader's open orders for one line's Kalshi ticker. */
  userOrdersFor: (ticker: string) => UserOrderSlice[] | null;
  /** Level click, tagged with the block's own line index. */
  onLevelClick: (
    lineIdx: number,
    side: 'yes' | 'no',
    levelKind: 'bid' | 'ask',
    cents: number,
    liquiditySeed?: BookLiquiditySeed | null,
  ) => void;
  colSpan?: number;
  focused?: boolean;
  /** #609 — the focused card's id, so the expanded slot can carry the hotkey
   *  fence's marks. Null whenever hotkeys are off — the marks must never
   *  appear then. #953: the row carries only the card mark; the PRICE marks
   *  sit on the selected block's two best-bid prices (`lt2PriceMarks`),
   *  because this row spans a sideways scroller that can hide the selected
   *  block while the row itself is comfortably on screen. */
  lt2FocusId?: string | null;
  /** LT2-018 — NO/UNDER columns render left in every block. */
  flipColumns?: boolean;
  /** Forwarded to TwoSidedLadderBody — level-wide cancel on the rungs
   *  (live-game/sniper boards only; see TwoSidedLadderProps). */
  enableLevelCancel?: boolean;
  /**
   * Sniper v2 Phase 2 — make the SELECTED line's ladder cursor-addressable.
   *
   * Forwarded to the selected block ONLY, which is the panel's job rather than
   * the caller's: A/D have always resolved the selected line alone (see the
   * header), so the card publishes one rung list, and the listener refuses any
   * rung painted more than once for the same (card, slot, level). Handing the
   * same addressing to all three blocks would duplicate every level and refuse
   * every press. Absent/null = no block addresses levels (the default, and
   * byte-identical to the pre-Phase-2 panel).
   */
  lt2Levels?: Lt2LadderAddressing | null;
  /** StreamRift skin: selected block only, cockpit rungs (see the header).
   *  Absent = `'default'`, byte-identical for the dashboard and /live. */
  variant?: 'default' | 'cockpit';
  /** Cockpit only — forwarded to the ladder (see TwoSidedLadderProps.hue). */
  hue?: LadderHue | null;
  /** Forwarded to the ladder — must equal the depth the card's resolver uses
   *  (see TwoSidedLadderProps.depth). Absent = `LADDER_LEVELS`. */
  depth?: number;
}

function sourceTime(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms) || Math.abs(ms) > 8.64e15) return 'Source time unavailable';
  return `As of ${new Date(ms).toISOString().slice(0, 23).replace('T', ' ')} UTC`;
}

function lineLabel(kind: 'spread' | 'total', line: SpreadLine | TotalLine): string {
  return kind === 'spread'
    ? formatSignedLine((line as SpreadLine).line)
    : String((line as TotalLine).line);
}

export function NearestLinesPanel({
  event,
  period = 'full',
  kind,
  lines,
  selectedIdx,
  yesLabel,
  noLabel,
  makerRebatePct,
  userOrdersFor,
  onLevelClick,
  colSpan = 12,
  focused = false,
  lt2FocusId = null,
  flipColumns = false,
  enableLevelCancel = false,
  lt2Levels = null,
  variant = 'default',
  hue = null,
  depth,
}: NearestLinesPanelProps) {
  const cockpit = variant === 'cockpit';
  const butterfly = event != null && !cockpit;
  const version = useMarketStore((s) => s.version);
  const spreadReader = useMarketStore((s) => s.getOpSpreadLineFor);
  const marketsReader = useMarketStore((s) => s.getOpMarketsFor);
  const totalReader = useMarketStore((s) => s.getOpTotalLineFor);
  const showReference = useDisplaySettings((s) => s.showOpOdds);
  // #1053 Part 1: the window is over the lines that carry a Kalshi book —
  // a Poly-only line never displaces a Kalshi line from it.
  const window = useMemo(
    () => (cockpit ? [selectedIdx] : venueAwareLineWindow(lines, selectedIdx)),
    [cockpit, lines, selectedIdx],
  );
  const contexts = useMemo(() => {
    void version;
    if (!butterfly || event == null) return new Map<number, ReturnType<typeof butterflyCardContext>>();
    return new Map(window.map(index => [index, butterflyCardContext(event, kind, lines[index],
      { spread: spreadReader, total: totalReader, markets: marketsReader }, period)]));
  }, [butterfly, event, kind, lines, period, version, spreadReader, totalReader, marketsReader, window]);
  const marks = focused && lt2FocusId ? lt2FocusId : undefined;
  const [rowRef, measuredOverflow] = useRowOverflow();
  const rowOverflows = butterfly && measuredOverflow;
  const selectedRef = useScrollSelectedIntoView(rowRef, contexts.get(selectedIdx)?.key ?? null);
  return (
    <tr
      className={cn('border-0', focused && !cockpit && '!bg-primary/15')}
      data-testid="nearest-lines-panel"
      data-lt2-focused={marks}
      // #953: butterfly (narrow) blocks mark the selected block's own two
      // best-bid prices. The default-layout ladders (the live-game / Sniper
      // boards) carry no per-price marks, so their row keeps the whole-row
      // price mark it always had.
      data-lt2-price={marks == null || butterfly ? undefined : ''}
      data-lt2-hotkey-control={marks == null ? undefined : ''}
    >
      <Td colSpan={colSpan} className={cockpit ? 'p-0' : 'py-1.5'}>
        {rowOverflows && (
          <div
            data-testid="butterfly-scroll-signal"
            className="text-muted-foreground mb-1 px-2 text-[10px] leading-4"
          >
            Scroll to view all three lines.
          </div>
        )}
        <div
          ref={rowRef}
          data-testid={butterfly ? 'butterfly-row' : undefined}
          data-overflowing={rowOverflows ? '' : undefined}
          // Butterfly — `w-0 min-w-full`: the row contributes no intrinsic
          // width to its table cell, so three cards cannot widen the table
          // (which at 1470 would clip the third card behind the table's own
          // scroller with nothing saying so); they overflow THIS row, which
          // scrolls and is signaled above.
          className={
            cockpit
              ? 'flex'
              : butterfly
                ? 'flex w-0 min-w-full items-stretch gap-2 overflow-x-auto px-2 [scrollbar-gutter:stable]'
                : 'flex items-stretch gap-3 overflow-x-auto px-2'
          }
        >
          {window.map((idx) => {
            const line = lines[idx];
            if (!line) return null;
            const context = contexts.get(idx) ?? null;
            const kalshi = context ? context.book : (line.kalshi ?? null);
            const availability = classifyLineAvailability(kalshi);
            const isSelected = idx === selectedIdx;
            return (
              <div
                key={context ? context.key : idx}
                ref={isSelected ? selectedRef : undefined}
                data-butterfly-market={context ? (context.ticker ?? 'absent') : undefined}
                data-butterfly-identity={context ? context.key : undefined}
                data-testid={`nearest-line-block-${idx}`}
                className={
                  cockpit
                    ? 'min-w-0 flex-1'
                    : cn(
                        // Butterfly: the narrow book's own width plus card
                        // padding — the card never squeezes its rungs (#884
                        // blocker), it overflows the row instead, which is
                        // signaled above.
                        context ? 'min-w-[30rem]' : 'min-w-[280px]',
                        'flex-1 rounded border p-1.5',
                        isSelected ? 'border-primary/50 bg-primary/5' : 'border-border/40',
                      )
                }
              >
                {cockpit ? (
                  availability !== 'active' && (
                    <div
                      className="bg-warn/10 text-warn px-4 py-1.5 text-[12px] font-semibold"
                      title="Order entry disabled on this line"
                    >
                      {LINE_AVAILABILITY_LABEL[availability]}
                    </div>
                  )
                ) : (
                  <div className={cn('mb-1 flex items-center gap-1.5', context && 'h-6')}>
                    <span
                      className={cn(
                        'font-mono text-xs font-semibold',
                        isSelected ? 'text-primary' : 'text-foreground',
                      )}
                    >
                      {context && kind === 'spread' && flipColumns
                        ? formatSignedLine(-line.line)
                        : lineLabel(kind, line)}
                    </span>
                    {isSelected && (
                      <span className="text-muted-foreground text-[9px] uppercase tracking-wide">
                        selected
                      </span>
                    )}
                    {availability !== 'active' && (
                      <span
                        className="rounded border border-amber-500/40 bg-amber-500/10 px-1 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-500"
                        title="Order entry disabled on this line"
                      >
                        {LINE_AVAILABILITY_LABEL[availability]}
                      </span>
                    )}
                  </div>
                )}
                {context && (
                <div className="mb-1 space-y-0.5 text-xs leading-4" data-butterfly-context="">
                  <div className="font-semibold">YES {context.yesLabel} · NO {context.noLabel}</div>
                  {/* The badge above already names a non-active state; only
                      "active" has nowhere else to show. */}
                  <div className="text-muted-foreground">
                    Kalshi · {period === 'full' ? 'Full game' : period}
                    {context.availability === 'active' && ' · active'}
                  </div>
                  <div className="flex flex-wrap gap-x-3 font-mono tabular-nums">
                    <span>{context.volumeKind}: {context.volume == null ? '—' : formatVolume(context.volume)}</span>
                    <span>Resting $: {context.liquidity == null ? '—' : formatCompactCurrency(context.liquidity)}</span>
                  </div>
                  {/* #884 — the reference block is five constant-height blocks (six visual lines)
                      (values, Pinnacle time, source, YES time, NO time) so a routine update —
                      a second timestamp appearing, a withdrawal or push note
                      arriving, a source changing — never changes its height
                      and never moves the executable prices under it. The
                      source line stays one line; its flags come first so a
                      long anchor name is what gives way, and the full text
                      rides the title. Both times are always printed. */}
                  {showReference && (() => {
                    const sourceText =
                      context.reference.yes.source == null && context.reference.no.source == null
                        ? 'Exact reference unavailable'
                        : [
                            context.reference.pinnacleWithdrawn ? 'Pinnacle withdrawn' : null,
                            context.reference.canPush ? 'Can push' : null,
                            [...new Set([context.reference.yes.source, context.reference.no.source].filter(Boolean))].join(' / '),
                          ]
                            .filter(Boolean)
                            .join(' · ');
                    return (
                      <div className="rounded border border-border/40 px-1 py-0.5" data-butterfly-reference="">
                        <div className="flex justify-between gap-2 font-mono tabular-nums">
                          <span>YES {fairPctText(context.reference.yes.tenths) == null ? '—' : `${fairPctText(context.reference.yes.tenths)}%`}</span>
                          <span>NO {fairPctText(context.reference.no.tenths) == null ? '—' : `${fairPctText(context.reference.no.tenths)}%`}</span>
                        </div>
                        <div className="font-mono tabular-nums whitespace-nowrap" data-butterfly-pinnacle-time="">Pinnacle {sourceTime(context.fairView?.anchorUpdatedAtMs ?? null)}</div>
                        <div className="text-muted-foreground overflow-hidden text-ellipsis whitespace-nowrap" title={sourceText} data-butterfly-source="">
                          {sourceText}
                        </div>
                        <div className="text-muted-foreground font-mono tabular-nums whitespace-nowrap" data-butterfly-time="yes">
                          YES {sourceTime(context.reference.yes.updatedAtMs)}
                        </div>
                        <div className="text-muted-foreground font-mono tabular-nums whitespace-nowrap" data-butterfly-time="no">
                          NO {sourceTime(context.reference.no.updatedAtMs)}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                )}
                <TwoSidedLadderBody
                  kalshi={kalshi}
                  yesLabel={yesLabel}
                  noLabel={noLabel}
                  userOrders={kalshi ? userOrdersFor(kalshi.marketTicker) : null}
                  availability={availability}
                  makerRebatePct={makerRebatePct}
                  onLevelClick={(side, levelKind, cents, liquiditySeed) =>
                    liquiditySeed ? onLevelClick(idx, side, levelKind, cents, liquiditySeed) : onLevelClick(idx, side, levelKind, cents)
                  }
                  flipColumns={flipColumns}
                  enableLevelCancel={enableLevelCancel}
                  lt2Levels={isSelected ? lt2Levels : null}
                  variant={variant}
                  hue={hue}
                  depth={depth}
                  presentation={context ? BUTTERFLY_NARROW_LADDER : undefined}
                  reference={
                    context && showReference
                      ? { fair: context.fairView, yesIsAway: context.yesIsAway, strike: context.strike }
                      : undefined
                  }
                  lt2PriceMarks={isSelected && context ? (marks ?? null) : null}
                />
              </div>
            );
          })}
          {butterfly &&
            Array.from({ length: Math.max(0, 3 - window.length) }, (_, i) => (
              <div key={`missing-${i}`} data-testid="butterfly-missing-alternate" className="min-w-[30rem] flex-1 rounded border border-dashed border-border/40 p-2 text-xs text-muted-foreground">
                No additional Kalshi line
              </div>
            ))}
        </div>
      </Td>
    </tr>
  );
}
